/**
 * RadarConfigPage — `/ivt/radar-config`.
 *
 * The 资产雷达偏好设置 Admin page: one industry at a time, three sections:
 *   1. 适用于本行业全部内容 — the single GENERAL_TOPIC row's keyword lists
 *      PLUS 资讯提取规则 / 方向归纳规则 (Xin 2026-10-29 overrode the
 *      Figma, which left ext/ind off this block). GENERAL has no SetStatus
 *      op: it is a non-deletable constant row, so this block is always
 *      present when the industry has been seeded, and absent otherwise.
 *   2. 信息源 — the NEWS_SOURCE table. Per-row Add / Edit / Delete fires
 *      the Admin ops immediately against the client (per-row delete is
 *      SetIndustryNewsSourceStatus=INACTIVE, soft delete).
 *   3. 设置主题推荐 — up to 10 STANDARD_TOPIC blocks (title + ext_rule +
 *      ind_rule + keyword lists). 增加新主题 adds a blank block; Remove
 *      drops an unsaved block or stages a loaded one for SetStatus=INACTIVE
 *      on Confirm.
 *
 * Confirm is a BATCH over GENERAL + topics only. News-source edits do
 * NOT wait for Confirm — see useRadarConfig for why.
 *
 * Access: any IVT group reaches the page (per topic-model §3.3 the list
 * ops require only `ivthub`); the writes require editor/admin + grant, so
 * a viewer's Confirm will 403 and the hook surfaces that denial. The nav
 * tab is hidden from the layout; the entry is the Dashboard's 偏好设置
 * gear button (R17 wires that link).
 *
 * Industry picker: this page uses the caller's own allowed-industries
 * (GetAllowedIndustries). An industry picked here is NOT persisted to
 * Dashboard's picker — they are owned by their respective pages so a
 * "quick change to see how it reads" never bounces the main view back to
 * the first industry.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  Textarea,
  TokenGroup,
  Form,
  FormField,
  BreadcrumbGroup,
} from '@amzn/awsui-components-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  RadarNewsSourceKindAdmin,
  RadarNewsSourceLocale,
  type RadarNewsSource,
  type RadarNewsSourceDraft,
} from '../../client/IvtHubClient';
import { useRadarConfig, pickLastUpdated } from './useRadarConfig';
import {
  initialFormState,
  addEmptyTopic,
  removeTopic,
  buildSubmitPlan,
  normalizeKeywords,
  projectActiveTopicCount,
  MAX_ACTIVE_TOPICS,
  type RadarConfigFormState,
  type TopicDraftRow,
} from './formState';
import { useAllowedIndustries } from '../DashboardPage/useAllowedIndustries';
import './RadarConfigPage.css';

const PAGE_TITLE = '雷达资讯偏好设置';

const KIND_LABELS: Record<string, string> = {
  GOOGLE_NEWS_KEYWORD: 'Google 新闻 · 关键词',
  GOOGLE_NEWS_SITE: 'Google 新闻 · 域名',
};
const LOCALE_LABELS: Record<string, string> = {
  'zh-CN': '中文（zh-CN）',
  'en-US': '英文（en-US）',
};

/** Keyword chip editor. Pressing Enter in the Input commits a token. */
function KeywordEditor({
  values,
  onChange,
  placeholder,
  disabled,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const commit = useCallback(() => {
    const next = normalizeKeywords([...values, draft]);
    if (next.length !== values.length) onChange(next);
    setDraft('');
  }, [draft, onChange, values]);
  return (
    <SpaceBetween size="xs">
      <Input
        value={draft}
        placeholder={placeholder ?? '输入后按回车添加'}
        onChange={({ detail }) => setDraft(detail.value)}
        onBlur={() => commit()}
        onKeyDown={(event) => {
          // Cloudscape maps Enter -> "Enter". A trailing comma commits too.
          // preventDefault() stops the comma character from landing in the
          // Input AFTER setDraft('') — otherwise the next token always
          // starts with a stray ','.
          if (event.detail.key === 'Enter' || event.detail.key === ',') {
            event.preventDefault();
            commit();
          }
        }}
        disabled={disabled}
      />
      <TokenGroup
        items={values.map((v) => ({ label: v, dismissLabel: `移除 ${v}` }))}
        onDismiss={({ detail }) => {
          const next = values.filter((_, i) => i !== detail.itemIndex);
          onChange(next);
        }}
      />
    </SpaceBetween>
  );
}

/** One STANDARD_TOPIC form block. */
function TopicBlock({
  row,
  index,
  onChange,
  onRemove,
  disabled,
}: {
  row: TopicDraftRow;
  index: number;
  onChange: (next: TopicDraftRow) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const update = (patch: Partial<TopicDraftRow>) => onChange({ ...row, ...patch });
  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <Button onClick={onRemove} disabled={disabled} ariaLabel={`移除主题 ${index + 1}`}>
              Remove
            </Button>
          }
        >
          {`主题 ${index + 1}`}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <FormField label="主题标题" errorText={row.title.trim() ? undefined : '必填'}>
          <Input
            value={row.title}
            onChange={({ detail }) => update({ title: detail.value })}
            placeholder="例如：医疗器械"
            disabled={disabled}
          />
        </FormField>
        <FormField label="资讯提取规则">
          <Textarea
            value={row.extractionRule}
            onChange={({ detail }) => update({ extractionRule: detail.value })}
            placeholder="描述需要抽取什么类型的资讯 / 需求"
            rows={3}
            disabled={disabled}
          />
        </FormField>
        <FormField label="方向归纳规则">
          <Textarea
            value={row.inductionRule}
            onChange={({ detail }) => update({ inductionRule: detail.value })}
            placeholder="描述方向归纳按什么维度做聚类"
            rows={3}
            disabled={disabled}
          />
        </FormField>
        <FormField label="搜索关键词" description="用, 区分多个关键词；最多 128 个">
          <KeywordEditor
            values={row.searchKeywords}
            onChange={(next) => update({ searchKeywords: next })}
            disabled={disabled}
          />
        </FormField>
        <FormField label="排除关键词" description="用, 区分多个关键词；最多 128 个">
          <KeywordEditor
            values={row.excludeKeywords}
            onChange={(next) => update({ excludeKeywords: next })}
            disabled={disabled}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}

interface NewsSourceDialogState {
  mode: 'create' | 'edit';
  source?: RadarNewsSource;
}

/** News-source add/edit dialog. Validates sourceLocator presence per sourceKind. */
function NewsSourceDialog({
  state,
  onCancel,
  onSubmit,
  submitting,
  error,
}: {
  state: NewsSourceDialogState;
  onCancel: () => void;
  onSubmit: (draft: RadarNewsSourceDraft) => void;
  submitting: boolean;
  /** Last submit error, surfaced INSIDE the modal so the user sees it before Cancel. */
  error: string | null;
}) {
  const [title, setTitle] = useState(state.source?.title ?? '');
  const [kind, setKind] = useState<string>(state.source?.sourceKind ?? RadarNewsSourceKindAdmin.GOOGLE_NEWS_KEYWORD);
  const [locator, setLocator] = useState(state.source?.sourceLocator ?? '');
  const [locale, setLocale] = useState<string>(state.source?.sourceLocale ?? RadarNewsSourceLocale.ZH_CN);

  const needsLocator = kind === RadarNewsSourceKindAdmin.GOOGLE_NEWS_SITE;
  const canSubmit = title.trim().length > 0 && (!needsLocator || locator.trim().length > 0);

  return (
    <Modal
      visible
      onDismiss={onCancel}
      header={state.mode === 'create' ? '新增信息源' : '编辑信息源'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!canSubmit || submitting}
              loading={submitting}
              onClick={() =>
                onSubmit({
                  title: title.trim(),
                  sourceKind: kind as 'GOOGLE_NEWS_KEYWORD' | 'GOOGLE_NEWS_SITE',
                  sourceLocator: needsLocator ? locator.trim() : undefined,
                  sourceLocale: locale as 'zh-CN' | 'en-US',
                })
              }
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="保存失败">
            {error}
          </Alert>
        )}
        <FormField label="来源名称" errorText={title.trim() ? undefined : '必填'}>
          <Input value={title} onChange={({ detail }) => setTitle(detail.value)} />
        </FormField>
        <FormField label="类型">
          <Select
            selectedOption={{ value: kind, label: KIND_LABELS[kind] ?? kind }}
            options={Object.values(RadarNewsSourceKindAdmin).map((v) => ({
              value: v,
              label: KIND_LABELS[v] ?? v,
            }))}
            onChange={({ detail }) => setKind(detail.selectedOption.value ?? kind)}
          />
        </FormField>
        {needsLocator && (
          <FormField
            label="链接地址（域名）"
            description="例如：example.com"
            errorText={locator.trim() ? undefined : '必填'}
          >
            <Input value={locator} onChange={({ detail }) => setLocator(detail.value)} />
          </FormField>
        )}
        <FormField label="语言">
          <Select
            selectedOption={{ value: locale, label: LOCALE_LABELS[locale] ?? locale }}
            options={Object.values(RadarNewsSourceLocale).map((v) => ({
              value: v,
              label: LOCALE_LABELS[v] ?? v,
            }))}
            onChange={({ detail }) => setLocale(detail.selectedOption.value ?? locale)}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

export default function RadarConfigPage() {
  const navigate = useNavigate();
  const { sub } = useAuth();
  const { industries, status: industriesStatus } = useAllowedIndustries(sub, true);
  const [industry, setIndustry] = useState<string | null>(null);

  // Default to the first allowed industry once the permission call settles.
  useEffect(() => {
    if (industry || !industries || industries.length === 0) return;
    setIndustry(industries[0]);
  }, [industries, industry]);

  const config = useRadarConfig(industry);
  const [form, setForm] = useState<RadarConfigFormState | null>(null);
  const [dialog, setDialog] = useState<NewsSourceDialogState | null>(null);
  // Which industry's snapshot the current `form` was built from. A news-source
  // write triggers a snapshot reload inside the hook — that landed snapshot is
  // the SAME industry and MUST NOT wipe in-progress GENERAL / topic edits.
  // We rebuild the form only on the first ready snapshot of an industry or
  // on a Confirm-driven reload (tracked by `pendingConfirmReload`).
  const [formIndustry, setFormIndustry] = useState<string | null>(null);
  const pendingConfirmReload = useRef(false);

  useEffect(() => {
    if (config.status !== 'ready' || !config.snapshot) return;
    const changed =
      formIndustry !== config.snapshot.industry || pendingConfirmReload.current;
    if (changed) {
      setForm(initialFormState(config.snapshot.general, config.snapshot.topics));
      setFormIndustry(config.snapshot.industry);
      pendingConfirmReload.current = false;
    }
  }, [config.status, config.snapshot, formIndustry]);

  useEffect(() => {
    if (industry) config.loadIndustry(industry);
    // config.loadIndustry is stable; omit from deps to avoid a reload loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  // Use the single `projectActiveTopicCount` helper so the Add-disabled
  // state and `buildSubmitPlan`'s cap check agree on the count. Blank
  // new rows do not contribute (both call sites ignore them), which lets
  // a user swap one row for another at the cap without filling the
  // placeholder first.
  const projectedTopicCount = useMemo(() => {
    if (!form || !config.snapshot) return 0;
    return projectActiveTopicCount(config.snapshot.topics, form);
  }, [form, config.snapshot]);
  const addDisabled = !form || projectedTopicCount >= MAX_ACTIVE_TOPICS;
  const lastUpdated = pickLastUpdated(config.snapshot);

  const plan = useMemo(() => {
    if (!form || !config.snapshot) return null;
    return buildSubmitPlan({
      initialGeneral: config.snapshot.general,
      initialTopics: config.snapshot.topics,
      state: form,
    });
  }, [form, config.snapshot]);

  const [submitError, setSubmitError] = useState<string | null>(null);
  // News-source dialog errors go HERE and are rendered INSIDE the modal — a
  // throw from create/edit does not close the dialog, so a shared submitError
  // (which is painted by the <Form errorText>) would sit behind the modal and
  // leave the user staring at a frozen Save button with no feedback.
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Disable every input / action while a submit OR the follow-up refresh
  // is in flight. The hook flips `submitting` false before `setReloadKey`
  // resolves, so a user re-focusing an Input and typing in that gap would
  // have their keystrokes clobbered by the form-rebuild gate when the
  // reloaded snapshot lands.
  const busy = config.submitting || config.refreshing;

  const handleConfirm = useCallback(async () => {
    if (!plan) return;
    setSubmitError(null);
    // Arm the form-rebuild gate ONLY when at least one step actually
    // landed on the service. A pure-failure Confirm (e.g. viewer 403 on
    // the first step) commits nothing, so the reloaded snapshot would be
    // byte-identical to the pre-submit one — rebuilding the form from it
    // would silently discard every in-progress edit for no gain. The
    // hook's submit reports `stepsCommitted`, and attaches it to any
    // thrown error so the catch branch sees the partial-vs-nothing split.
    try {
      const result = await config.submit(plan);
      if (result.stepsCommitted > 0) pendingConfirmReload.current = true;
    } catch (e) {
      const stepsCommitted = (e as { stepsCommitted?: number }).stepsCommitted ?? 0;
      if (stepsCommitted > 0) pendingConfirmReload.current = true;
      setSubmitError((e as Error).message);
    }
  }, [config, plan]);

  const handleCancel = useCallback(() => {
    // Reset the form to the loaded snapshot — same as discarding uncommitted edits.
    if (config.snapshot) {
      setForm(initialFormState(config.snapshot.general, config.snapshot.topics));
    }
    setSubmitError(null);
  }, [config.snapshot]);

  if (industriesStatus === 'loading') {
    return (
      <div className="radar-config">
        <Spinner /> Loading…
      </div>
    );
  }
  if (industriesStatus === 'failed' || !industries || industries.length === 0) {
    return (
      <div className="radar-config">
        <Alert type="error" header="无法读取可访问的行业">
          请确认你拥有 `ivthub` 组权限并至少被授予一个行业的 grant。
        </Alert>
      </div>
    );
  }

  return (
    <div className="radar-config">
      <BreadcrumbGroup
        items={[
          { text: 'Dashboard', href: '/ivt/dashboard' },
          { text: PAGE_TITLE, href: '' },
        ]}
        onFollow={(e) => {
          e.preventDefault();
          if (e.detail.href) navigate(e.detail.href);
        }}
      />
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <Select
              selectedOption={
                industry
                  ? { value: industry, label: industry }
                  : { value: '', label: '选择行业' }
              }
              options={industries.map((v) => ({ value: v, label: v }))}
              onChange={({ detail }) => {
                // Switching industry: wipe any error that belonged to the
                // previous industry so the new form does not open with a
                // stale red banner from the last Confirm failure.
                setSubmitError(null);
                setDialogError(null);
                setIndustry(detail.selectedOption.value ?? null);
              }}
              ariaLabel="行业选择"
            />
          }
        >
          {PAGE_TITLE}
        </Header>
        {lastUpdated?.at && (
          <Box variant="small" color="text-body-secondary">
            最后更新：{lastUpdated.at.toLocaleString('zh-CN')}
            {lastUpdated.by ? ` · ${lastUpdated.by}` : null}
          </Box>
        )}

        {config.status === 'loading' && (
          <div>
            <Spinner /> 载入 {industry} 配置…
          </div>
        )}

        {config.status === 'failed' && (
          <Alert
            type="error"
            header="载入失败"
            action={
              config.denial === null ? (
                <Button onClick={config.reload}>重试</Button>
              ) : undefined
            }
          >
            {config.error ?? '未知错误'}
          </Alert>
        )}

        {config.status === 'ready' && form && config.snapshot && (
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={handleCancel} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={!plan || plan.errors.length > 0 || plan.steps.length === 0}
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>
              </SpaceBetween>
            }
            errorText={submitError ?? plan?.errors[0]}
          >
            <SpaceBetween size="xl">
              {/* GENERAL_TOPIC block */}
              <Container header={<Header variant="h2">适用于本行业全部内容</Header>}>
                {form.general ? (
                  <SpaceBetween size="l">
                    <FormField label="资讯提取规则">
                      <Textarea
                        value={form.general.extractionRule}
                        onChange={({ detail }) =>
                          setForm((f) =>
                            f && f.general
                              ? { ...f, general: { ...f.general, extractionRule: detail.value } }
                              : f,
                          )
                        }
                        rows={3}
                        disabled={busy}
                      />
                    </FormField>
                    <FormField label="方向归纳规则">
                      <Textarea
                        value={form.general.inductionRule}
                        onChange={({ detail }) =>
                          setForm((f) =>
                            f && f.general
                              ? { ...f, general: { ...f.general, inductionRule: detail.value } }
                              : f,
                          )
                        }
                        rows={3}
                        disabled={busy}
                      />
                    </FormField>
                    <FormField label="搜索关键词" description="用, 区分多个关键词；最多 128 个">
                      <KeywordEditor
                        values={form.general.searchKeywords}
                        onChange={(next) =>
                          setForm((f) =>
                            f && f.general ? { ...f, general: { ...f.general, searchKeywords: next } } : f,
                          )
                        }
                        disabled={busy}
                      />
                    </FormField>
                    <FormField label="排除关键词" description="用, 区分多个关键词；最多 128 个">
                      <KeywordEditor
                        values={form.general.excludeKeywords}
                        onChange={(next) =>
                          setForm((f) =>
                            f && f.general ? { ...f, general: { ...f.general, excludeKeywords: next } } : f,
                          )
                        }
                        disabled={busy}
                      />
                    </FormField>
                  </SpaceBetween>
                ) : (
                  <Alert type="warning" header="GENERAL_TOPIC 未初始化">
                    该行业尚未执行 M1 seed，无法编辑通用主题。请联系 IVT Hub 团队运行 seed 脚本后再回来。
                  </Alert>
                )}
              </Container>

              {/* News sources */}
              <Container
                header={
                  <Header
                    variant="h2"
                    counter={`(${config.snapshot.newsSources.length})`}
                    actions={
                      <Button
                        iconName="add-plus"
                        onClick={() => setDialog({ mode: 'create' })}
                        disabled={busy}
                      >
                        新增资源
                      </Button>
                    }
                  >
                    信息源
                  </Header>
                }
              >
                <Table
                  variant="embedded"
                  items={config.snapshot.newsSources}
                  trackBy="sourceId"
                  columnDefinitions={[
                    { id: 'title', header: '来源', cell: (r) => r.title ?? '' },
                    {
                      id: 'locator',
                      header: '链接地址',
                      cell: (r) => r.sourceLocator ?? '-',
                    },
                    {
                      id: 'kind',
                      header: '类型',
                      cell: (r) => (r.sourceKind ? KIND_LABELS[r.sourceKind] ?? r.sourceKind : ''),
                    },
                    {
                      id: 'locale',
                      header: '语言',
                      cell: (r) => (r.sourceLocale ? LOCALE_LABELS[r.sourceLocale] ?? r.sourceLocale : ''),
                    },
                    {
                      id: 'actions',
                      header: '',
                      cell: (r) => (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            variant="icon"
                            iconName="edit"
                            ariaLabel={`编辑 ${r.title}`}
                            onClick={() => setDialog({ mode: 'edit', source: r })}
                            disabled={busy}
                          />
                          <Button
                            variant="icon"
                            iconName="remove"
                            ariaLabel={`删除 ${r.title}`}
                            onClick={() => {
                              if (!r.sourceId) return;
                              // Destructive write (soft-delete) — confirm
                              // before firing. ConfigurationPage uses a full
                              // Modal for industry delete; news-source delete
                              // has a lower blast radius (recoverable by
                              // re-creating the row) so a native confirm is
                              // enough — the gate just has to exist.
                              if (
                                !window.confirm(`确认删除信息源「${r.title ?? ''}」？`)
                              ) {
                                return;
                              }
                              config
                                .removeNewsSource(r.sourceId)
                                .catch((e) => setSubmitError((e as Error).message));
                            }}
                            disabled={busy}
                          />
                        </SpaceBetween>
                      ),
                    },
                  ]}
                  empty={<Box textAlign="center">暂无信息源。</Box>}
                />
              </Container>

              {/* Standard topics */}
              <Container
                header={
                  <Header
                    variant="h2"
                    description={`最多 ${MAX_ACTIVE_TOPICS} 个主题`}
                    counter={`(${form.topics.length})`}
                  >
                    设置主题推荐
                  </Header>
                }
              >
                <SpaceBetween size="l">
                  {form.topics.map((row, i) => (
                    <TopicBlock
                      key={row.localKey}
                      row={row}
                      index={i}
                      onChange={(next) =>
                        setForm((f) =>
                          f
                            ? {
                                ...f,
                                topics: f.topics.map((r) => (r.localKey === row.localKey ? next : r)),
                              }
                            : f,
                        )
                      }
                      onRemove={() =>
                        setForm((f) => (f ? removeTopic(f, row.localKey) : f))
                      }
                      disabled={busy}
                    />
                  ))}
                  <Button
                    iconName="add-plus"
                    onClick={() => setForm((f) => (f ? addEmptyTopic(f) : f))}
                    disabled={addDisabled || busy}
                  >
                    增加新主题
                  </Button>
                  {addDisabled && (
                    <Box variant="small" color="text-status-info">
                      已达到 {MAX_ACTIVE_TOPICS} 个主题上限
                    </Box>
                  )}
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          </Form>
        )}
      </SpaceBetween>

      {dialog && (
        <NewsSourceDialog
          state={dialog}
          error={dialogError}
          onCancel={() => {
            setDialog(null);
            setDialogError(null);
          }}
          submitting={busy}
          onSubmit={async (draft) => {
            setDialogError(null);
            try {
              if (dialog.mode === 'create') {
                await config.createNewsSource(draft);
              } else if (dialog.source?.sourceId) {
                await config.updateNewsSource(dialog.source.sourceId, draft);
              }
              setDialog(null);
            } catch (e) {
              setDialogError((e as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}
